import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ExternalLink, Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { validateMetaAdDraft } from '../../lib/metaAdValidation';
import type { MetaAdValidationIssue } from '../../lib/metaAdValidation';
import type { MetaAdDraft, MetaAdDraftInput, MetaAdPublishStatus, PublishError } from '../../types/metaAdDraft';
import {
  duplicateDraft,
  getDraft,
  getEligibleAccounts,
  patchDraft,
  publishDraft,
  PublishFailedError,
  type PublishSuccess,
} from './api';
import { draftToInput } from './emptyDraft';
import { computeSaveStatus, SAVE_STATUS_LABEL } from './saveStatus';
import { useLinkedAdAccount } from './useLinkedAdAccount';
import { publishDisabledReason } from './publishButtonState';
import {
  buildPublishSummary,
  creativeWarnings,
  metaAdsManagerUrl,
  multiTextWarnings,
  placementWarnings,
} from './publishSummary';
import { CampaignSection } from './sections/CampaignSection';
import { AdSetSection } from './sections/AdSetSection';
import { AdSection } from './sections/AdSection';
import { PreviewPanel } from './components/PreviewPanel';
import { PublishStatusToggle } from './components/PublishStatusToggle';
import { PublishConfirmationDialog } from './components/PublishConfirmationDialog';
import { PublishSuccessModal } from './components/PublishSuccessModal';
import { PublishErrorModal } from './components/PublishErrorModal';
import { DuplicateModal } from './components/DuplicateModal';
import { PullCreativeModal } from './PullCreativeModal';
import { AttachTargetSummary } from './components/AttachTargetSummary';
import { AttachMediaChoice } from './components/AttachMediaChoice';
import { withMediaMode } from './liveAdSetFlow';
import { mediaModeOf } from '../../lib/metaAttachMedia';
import { AttachPublishConfirmationDialog } from './components/AttachPublishConfirmationDialog';

const AUTOSAVE_DEBOUNCE_MS = 10_000;

// Fix B — reconcile a publish whose HTTP request died on a transport timeout (a
// long publish exceeding the ~100s Cloudflare proxy timeout while the server keeps
// finalizing). Poll the draft's true status until it settles. ~5 min cap at 5s.
const PUBLISH_RECONCILE_INTERVAL_MS = 5_000;
const PUBLISH_RECONCILE_MAX_ATTEMPTS = 60;
const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

const NAV = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'adset', label: 'Ad Sets' },
  { id: 'ad', label: 'Ad' },
  { id: 'preview', label: 'Preview' },
];

export function MetaAdCompose() {
  const { draftId } = useParams<{ draftId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const existing = useQuery({
    queryKey: ['metaAdDraft', draftId],
    queryFn: () => getDraft(draftId!),
    enabled: Boolean(draftId),
  });

  const [input, setInput] = useState<MetaAdDraftInput | null>(null);
  const [warnings, setWarnings] = useState<MetaAdValidationIssue[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  const versionRef = useRef(0);
  const lastSavedRef = useRef<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const seeded = useRef(false);
  const reconcileRef = useRef(false); // guards against overlapping reconcile polls (Fix B)

  useEffect(() => {
    if (existing.data && !seeded.current) {
      const seededInput = draftToInput(existing.data.draft);
      setInput(seededInput);
      setWarnings(existing.data.composeWarnings);
      versionRef.current = existing.data.draft.version;
      lastSavedRef.current = JSON.stringify(seededInput);
      seeded.current = true;
    }
  }, [existing.data]);

  const update = (patch: Partial<MetaAdDraftInput>) => setInput((d) => (d ? { ...d, ...patch } : d));
  // Publish Active/Off — one campaign-wide value, bound to BOTH toggles (top + the
  // publish card) so flipping either reflects in both.
  const setPublishStatus = (publishStatus: MetaAdPublishStatus) =>
    setInput((d) => (d ? { ...d, campaign: { ...d.campaign, publishStatus } } : d));

  const inputJson = input ? JSON.stringify(input) : '';
  const isDirty = Boolean(input) && lastSavedRef.current !== inputJson;
  const canSave = Boolean(input && input.title.trim().length > 0);
  // The AUTHORITATIVE draft state (from the server, kept fresh by setQueryData on
  // publish + refetch). Autosave gates on this — NOT on publish.isPending, which
  // reflects only the client request and flips false when a long publish exceeds the
  // ~100s proxy timeout while the server is still finalizing (the 409-storm bug).
  const serverStatus = existing.data?.draft.status ?? 'draft';

  const save = useMutation({
    mutationFn: async () => {
      if (!draftId || !input) throw new Error('No draft.');
      pendingRef.current = JSON.stringify(input);
      try {
        return await patchDraft(draftId, input, versionRef.current);
      } catch (e) {
        // Version conflict: refetch the server's version + retry once.
        if (e instanceof ApiError && e.status === 409 && e.message === 'version_conflict') {
          const fresh = await getDraft(draftId);
          versionRef.current = fresh.draft.version;
          return await patchDraft(draftId, input, fresh.draft.version);
        }
        throw e;
      }
    },
    onSuccess: ({ draft, composeWarnings }) => {
      versionRef.current = draft.version;
      lastSavedRef.current = pendingRef.current;
      setWarnings(composeWarnings);
      setSaveError(null);
      setHasSavedOnce(true);
      qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
    },
    onError: (e) => {
      setSaveError(
        e instanceof ApiError && e.message === 'locked'
          ? 'This draft is published and can no longer be edited.'
          : e instanceof ApiError
            ? 'This draft was changed elsewhere — reload to continue.'
            : String(e),
      );
    },
  });

  // ---------- D3: publish flow ----------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successData, setSuccessData] = useState<PublishSuccess | null>(null);
  const [errorData, setErrorData] = useState<PublishError | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);

  // ---------- Build Campaign Phase 2: pull creative from Drive ----------
  const [pullOpen, setPullOpen] = useState(false);

  // ---------- D2.16: duplicate (published → fresh editable draft) ----------
  const [dupOpen, setDupOpen] = useState(false);
  const duplicate = useMutation({
    mutationFn: (newTitle: string) => duplicateDraft(draftId!, newTitle),
    onSuccess: ({ draft }) => {
      qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
      setDupOpen(false);
      navigate(`/advertising/meta/compose/${draft.id}`);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not duplicate this campaign.'),
  });

  // Fold the server-owned per-ad-set / per-ad publishState from a fetched draft into
  // the local input so the Published/Failed chips render for a retry, preserving any
  // unsaved edits. Shared by the structured-failure path + the reconcile-failure path.
  const foldPublishState = (draft: MetaAdDraft) => {
    setInput((prev) =>
      prev
        ? {
            ...prev,
            adSets: prev.adSets.map((a) => {
              const fresh = draft.adSets.find((f) => f.id === a.id);
              return fresh ? { ...a, publishState: fresh.publishState } : a;
            }),
            ads: {
              ...prev.ads,
              additionalAds: (prev.ads.additionalAds ?? []).map((a) => {
                const fresh = (draft.ads.additionalAds ?? []).find((f) => f.id === a.id);
                return fresh ? { ...a, publishState: fresh.publishState } : a;
              }),
            },
          }
        : prev,
    );
  };

  // Fix B — a publish whose HTTP request died on a TRANSPORT timeout (a long publish
  // exceeding the ~100s proxy timeout while the server keeps finalizing) may well have
  // SUCCEEDED. Rather than declaring failure (which dirties the input + triggers the
  // autosave storm), poll the draft's TRUE status until it settles: published →
  // success; reverted to draft (+ error) → the real failure; still publishing → wait.
  const reconcilePublish = async (loadingToastId: string | number) => {
    if (!draftId || reconcileRef.current) {
      toast.dismiss(loadingToastId);
      return;
    }
    reconcileRef.current = true;
    try {
      for (let attempt = 0; attempt < PUBLISH_RECONCILE_MAX_ATTEMPTS; attempt += 1) {
        let draft: MetaAdDraft;
        try {
          const res = await getDraft(draftId);
          draft = res.draft;
          versionRef.current = res.draft.version;
          // Refresh the cache so serverStatus gates autosave + the UI shows the live
          // 'publishing' → 'published' transition without a manual reload.
          qc.setQueryData(['metaAdDraft', draftId], { draft: res.draft, composeWarnings: res.composeWarnings });
        } catch {
          await sleep(PUBLISH_RECONCILE_INTERVAL_MS);
          continue;
        }

        if (draft.status === 'published') {
          toast.dismiss(loadingToastId);
          setErrorOpen(false);
          setErrorData(null);
          setSuccessData({
            publishedMetaAdId: draft.publishedMetaAdId,
            publishedMetaAdSetId: draft.publishedMetaAdSetId,
            publishedMetaCampaignId: draft.publishedMetaCampaignId,
            publishedAt: draft.publishedAt,
            draft,
            warnings: [],
          });
          qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
          toast.success('Ad published to Meta successfully');
          return;
        }
        if (draft.status !== 'publishing') {
          // Settled as a real failure (status reverted to 'draft' with publishError).
          toast.dismiss(loadingToastId);
          foldPublishState(draft);
          if (draft.publishError) {
            setErrorData(draft.publishError);
            setErrorOpen(true);
          } else {
            toast.error('Publishing did not complete — review the draft and try again.');
          }
          qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
          return;
        }
        // Still publishing — wait and poll again.
        await sleep(PUBLISH_RECONCILE_INTERVAL_MS);
      }
      // Never settled within the cap (rare — a server-side stall). Leave the cached
      // 'publishing' status (UI shows the banner); the AM can reload to recheck.
      toast.dismiss(loadingToastId);
      toast.message('Still publishing — reload in a moment to see the result.');
    } finally {
      reconcileRef.current = false;
    }
  };

  const publish = useMutation({
    mutationFn: (version: number) => publishDraft(draftId!, version),
    onSuccess: (data) => {
      setConfirmOpen(false);
      setErrorOpen(false);
      setErrorData(null);
      versionRef.current = data.draft.version;
      // Flip the cached draft to its published state → read-only + banner.
      qc.setQueryData(['metaAdDraft', draftId], {
        draft: data.draft,
        composeWarnings: [],
      });
      qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
      setSuccessData(data);
      toast.success('Ad published to Meta successfully');
    },
    onError: (err) => {
      setConfirmOpen(false);
      if (err instanceof PublishFailedError) {
        if (err.kind === 'conflict') {
          if (err.current) versionRef.current = err.current.version;
          toast.error('This draft changed elsewhere — version re-synced. Try publishing again.');
          return;
        }
        if (err.kind === 'locked') {
          toast.error('This draft is already published.');
          existing.refetch();
          return;
        }
        if (err.kind === 'in_progress') {
          toast.error('A publish is already in progress for this draft.');
          return;
        }
        if (err.kind === 'invalid') {
          toast.error('The ad failed validation — resolve the errors and try again.');
          return;
        }
        // kind === 'failed'. TWO cases:
        //  (a) TRANSPORT timeout — no structured body (publishError === null): the
        //      ~2.5min publish exceeded the ~100s proxy timeout while the server kept
        //      finalizing. It may have SUCCEEDED. Reconcile by polling the true status
        //      (Fix B) instead of declaring failure + dirtying the input.
        //  (b) STRUCTURED failure (publishError present): the server returned a real
        //      failure — show it + fold the per-ad publishState for the retry chips.
        if (err.publishError === null) {
          const tId = toast.loading('Finishing publish — confirming with Meta…');
          void reconcilePublish(tId);
          return;
        }
        setErrorData(err.publishError);
        setErrorOpen(true);
        if (draftId) {
          void getDraft(draftId)
            .then(({ draft, composeWarnings }) => {
              versionRef.current = draft.version;
              qc.setQueryData(['metaAdDraft', draftId], { draft, composeWarnings });
              foldPublishState(draft);
            })
            .catch(() => {});
        }
        return;
      }
      // Non-PublishFailedError (an unexpected throw). Could still be a transport blip
      // — reconcile rather than assume failure.
      const tId = toast.loading('Finishing publish — confirming with Meta…');
      void reconcilePublish(tId);
    },
  });

  // Autosave (debounced). Gated on the AUTHORITATIVE server status: NEVER autosave
  // unless the draft is 'draft'. A 'publishing'/'published' draft is locked server-
  // side, so a PATCH 409s ('locked') — and since the save never succeeds, isDirty
  // never clears, re-arming the 10s timer forever (the 409 storm). publish.isPending
  // still covers the in-flight window where serverStatus is a stale 'draft' (the
  // query isn't refetched mid-publish). openConfirm flushes a pending save before
  // publishing, so nothing is lost by pausing.
  useEffect(() => {
    if (!canSave || !isDirty || save.isPending || publish.isPending) return;
    if (serverStatus !== 'draft') return;
    const t = window.setTimeout(() => save.mutate(), AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inputJson captures input; save is stable
  }, [inputJson, canSave, isDirty, save.isPending, publish.isPending, serverStatus]);

  const status = computeSaveStatus({ saving: save.isPending, isDirty, error: Boolean(saveError), hasSavedOnce });

  // ---------- D1: preview validation + reach estimate ----------
  // Resolve the selected account's fbAdAccountId (eligible accounts already carry
  // it; the list is deduped with the Campaign picker's query). Drives both the
  // publish gate's account-connected check and reach-estimate enablement.
  const eligible = useQuery({ queryKey: ['metaAdEligibleAccounts'], queryFn: getEligibleAccounts });
  const selectedAccount =
    input && input.accountId
      ? eligible.data?.accounts.find((a) => a.id === input.accountId) ?? null
      : null;
  const { currency, timezoneName } = useLinkedAdAccount(input?.accountId ?? '');
  const draftStatus = existing.data?.draft.status ?? 'draft';

  const validation = useMemo(() => {
    if (!input) return null;
    return validateMetaAdDraft(input as unknown as MetaAdDraft, {
      account: selectedAccount ? { fbAdAccountId: selectedAccount.fbAdAccountId } : null,
      now: new Date(),
      // Schedule wall-clock resolves against the ad-account tz (same source as the
      // city-search scope), or Australian Eastern when the campaign override is on.
      scheduleTimezone: timezoneName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inputJson captures input
  }, [inputJson, selectedAccount?.fbAdAccountId, timezoneName]);

  if (draftId && existing.isLoading) return <CenteredSpinner />;
  if (draftId && existing.error) return <p className="text-sm text-red-600">Couldn't load this draft.</p>;
  if (!input) return <CenteredSpinner />;

  // Publish button state (D1) + D3 wiring.
  const v = validation ?? { ok: false, errors: [], warnings: [] };
  const publishReason = publishDisabledReason({
    draftStatus,
    validationOk: v.ok,
    errorCount: v.errors.length,
    // Multi-ad-set: reach is per-ad-set + advisory, so it no longer gates publish.
    reachLoading: false,
  });
  const publishDisabled = publishReason !== null;

  // Read-only when the server says published/publishing, OR while our own publish
  // request is in flight (the transient server 'publishing' only exists during it).
  const isPublishing = publish.isPending || draftStatus === 'publishing';
  const isPublished = draftStatus === 'published';
  const isReadOnly = isPublished || isPublishing;

  const liveDraft = existing.data?.draft ?? null;
  const adsManagerUrl = metaAdsManagerUrl(
    selectedAccount?.fbAdAccountId ?? null,
    liveDraft?.publishedMetaAdId ?? null,
  );

  // Open the confirmation — but first flush any unsaved edits, because the backend
  // builds the publish request from the STORED doc (local edits would be lost).
  const openConfirm = async () => {
    if (publishDisabled) return;
    if (isDirty) {
      try {
        await save.mutateAsync();
      } catch {
        return; // save.onError set saveError; don't open the dialog on stale data
      }
    }
    setConfirmOpen(true);
  };

  // Build Campaign Phase 2: open "Pull creative from Drive". Flush any unsaved edits
  // first (the modal patches the draft from the in-memory input + the current
  // version), then open. Adopt the patched draft so the builder reflects the new ads
  // without a reload + autosave doesn't fight it.
  const openPull = async () => {
    if (isDirty) {
      try {
        await save.mutateAsync();
      } catch {
        return; // save.onError surfaced the reason; don't open on stale data
      }
    }
    setPullOpen(true);
  };
  const adoptPulledDraft = (draft: MetaAdDraft) => {
    const next = draftToInput(draft);
    setInput(next);
    versionRef.current = draft.version;
    lastSavedRef.current = JSON.stringify(next);
    qc.setQueryData(['metaAdDraft', draftId], { draft, composeWarnings: [] });
    qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
  };

  // Close the dialog immediately and let the in-page "Publishing…" banner carry the
  // wait — a real publish runs for minutes (and reconciles past the proxy timeout),
  // so holding a popup open with a fake ETA is dishonest. Closes whichever modal is
  // open (confirm OR error-retry); the draft goes read-only via isPublishing.
  const confirmPublish = () => {
    setConfirmOpen(false);
    setErrorOpen(false);
    // Snap to the top so the "Publishing to Meta…" banner is in view for the wait.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    publish.mutate(versionRef.current);
  };

  const publishSummary = buildPublishSummary(input, {
    accountName: selectedAccount?.name ?? null,
    currency,
    verifiedAdvertiserId: selectedAccount?.metaVerifiedAdvertiserId ?? null,
    accountCountry: selectedAccount?.country ?? null,
  });
  const publishWarnings = [
    ...creativeWarnings(input.ads.creative),
    ...placementWarnings(input),
    ...multiTextWarnings(input),
  ];

  // "Add an ad to a live ad set": a different composer (locked target, one ad, attach-only).
  const attach = input.attachTarget ?? null;

  return (
    <div>
      <PageHeader
        title={input.title || 'Untitled draft'}
        description={
          attach
            ? 'Add an ad to a live ad set'
            : input.clonedFrom
              ? `Cloned from ${input.clonedFrom.campaignName || input.clonedFrom.campaignId}. The first publish is always paused.`
              : 'Compose and publish a Meta campaign'
        }
        backHref="/advertising/meta"
        actions={
          <div className="flex items-center gap-3">
            {/* Build Campaign Phase 2 — only on rollout-built drafts, while editable. */}
            {!attach && input.driveSource && !isReadOnly && (
              <Button variant="outline" onClick={openPull} disabled={save.isPending}>
                Pull creative from Drive
              </Button>
            )}
            <span className="text-sm text-slate-500" aria-live="polite">
              {SAVE_STATUS_LABEL[status]}
            </span>
          </div>
        }
      />

      {saveError && (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>
      )}

      {/* D3 TASK 7 — status banners */}
      {isPublished && (
        <div className="mb-4 flex items-start justify-between gap-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <div>
            <p className="font-semibold">Published</p>
            <p className="mt-0.5 text-green-700">
              This ad has been published. Editing requires creating a new draft.
              {liveDraft?.publishedAt ? ` Published ${new Date(liveDraft.publishedAt).toLocaleString()}.` : ''}
              {liveDraft?.publishedMetaAdId ? ` Meta ad ID: ${liveDraft.publishedMetaAdId}.` : ''}
            </p>
          </div>
          <Button variant="outline" className="shrink-0" onClick={() => setDupOpen(true)}>
            Duplicate as new
          </Button>
        </div>
      )}
      {isPublishing && !isPublished && (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
              Publishing your campaign to Meta — larger campaigns can take a few minutes.
            </span>
          </div>
          {/* Indeterminate progress (no fake ETA): a determinate "N of M ads" bar
              needs server-side progress, which ships with the async-publish follow-up. */}
          <div
            className="mt-2 h-1 w-full overflow-hidden rounded bg-slate-200"
            role="progressbar"
            aria-label="Publishing to Meta"
          >
            <div className="h-full w-1/3 animate-pulse rounded bg-kyck-500" />
          </div>
        </div>
      )}
      {!isReadOnly && liveDraft?.publishError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="font-medium">Last publish attempt failed:</span> {liveDraft.publishError.message}{' '}
          <button
            type="button"
            onClick={openConfirm}
            className="font-medium underline hover:text-red-900"
          >
            Try again
          </button>
        </div>
      )}

      {/* anchor nav — Preview stays disabled until validation passes */}
      <nav className="mb-6 flex gap-2 border-b border-slate-200 pb-3" aria-label="Compose sections">
        {NAV.filter((n) => !attach || n.id === 'ad').map((n) => {
          const previewLocked = n.id === 'preview' && !v.ok;
          return previewLocked ? (
            <span
              key={n.id}
              aria-disabled="true"
              title="Resolve errors before previewing"
              className="cursor-not-allowed rounded px-3 py-1.5 text-sm font-medium text-slate-300"
            >
              {n.label}
            </span>
          ) : (
            <a
              key={n.id}
              href={`#${n.id}`}
              className="rounded px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              {n.label}
            </a>
          );
        })}
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-10">
          {/* Editable region — a disabled fieldset natively disables every
              descendant control when published/publishing (no per-input wiring);
              pointer-events-none also neutralises div-based widgets. */}
          <fieldset
            disabled={isReadOnly}
            className={['m-0 min-w-0 space-y-10 border-0 p-0', isReadOnly ? 'pointer-events-none opacity-70' : ''].join(' ')}
          >
            {/* Draft title */}
            <div className="max-w-xl">
              <label htmlFor="draft-title" className="mb-1.5 block text-sm font-medium text-slate-700">
                Draft name
              </label>
              <input
                id="draft-title"
                type="text"
                value={input.title}
                onChange={(e) => update({ title: e.target.value })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              />
              <p className="mt-1 text-xs text-slate-400">Internal only — e.g. "Mount Druitt — Q3 Lead Gen".</p>
            </div>

            {/* Publish Active/Off — top toggle (same value as the publish card below). */}
            {attach ? (
              <>
                <AttachTargetSummary target={attach} />
                <AttachMediaChoice
                  target={attach}
                  onChange={(mode) => {
                    const next = withMediaMode(input, mode);
                    update({ attachTarget: next.attachTarget, ads: next.ads });
                  }}
                />
                <AdSection value={input} onChange={update} attachMode={{ destination: attach.destination, mediaMode: mediaModeOf(attach) }} />
              </>
            ) : (
              <>
                <PublishStatusToggle value={input.campaign.publishStatus} onChange={setPublishStatus} />

                <CampaignSection value={input} onChange={update} />
                <AdSetSection
                  value={input}
                  onChange={update}
                  issues={[...v.errors, ...v.warnings]}
                  accountName={selectedAccount?.name ?? ''}
                  fbAdAccountId={selectedAccount?.fbAdAccountId ?? null}
                />
                <AdSection value={input} onChange={update} />
              </>
            )}
          </fieldset>

          {!attach && <PreviewPanel input={input} validation={v} pageName={input.campaign.facebookPageName} />}
        </div>

        {/* right rail: status-aware publish card */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="space-y-4 rounded-md border border-slate-200 p-4">
            {isPublished ? (
              <div>
                <p className="mb-2 text-sm font-semibold text-green-700">Published to Meta</p>
                {liveDraft?.publishedMetaAdId && (
                  <p className="mb-3 break-all text-xs text-slate-500">
                    Ad ID: {liveDraft.publishedMetaAdId}
                  </p>
                )}
                {adsManagerUrl ? (
                  <Button asChild className="w-full">
                    <a href={adsManagerUrl} target="_blank" rel="noopener noreferrer">
                      View in Meta Ads Manager
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                ) : (
                  <p className="text-xs text-slate-400">Meta Ads Manager link unavailable.</p>
                )}
              </div>
            ) : isPublishing ? (
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Publishing…
              </div>
            ) : (
              <>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">Still to do</h3>
                  {warnings.length === 0 ? (
                    <p className="text-sm text-slate-500">Nothing flagged.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-slate-600">
                      {warnings.map((w) => (
                        <li key={w.code + w.field}>• {w.message}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  {/* Publish Active/Off — bottom toggle, next to the publish action
                      (same campaign.publishStatus value as the top toggle). */}
                  {!attach && (
                    <div className="mb-4">
                      <PublishStatusToggle value={input.campaign.publishStatus} onChange={setPublishStatus} />
                    </div>
                  )}
                  {v.ok ? (
                    <p className="mb-2 text-sm font-medium text-green-700">Ready to publish</p>
                  ) : (
                    <p className="mb-2 text-sm font-medium text-red-700">
                      {v.errors.length} error{v.errors.length === 1 ? '' : 's'} to resolve
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={publishDisabled}
                    title={publishReason ?? 'Publish to Meta'}
                    onClick={openConfirm}
                    className={[
                      'w-full rounded-md px-3 py-2 text-sm font-medium',
                      publishDisabled
                        ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                        : 'bg-kyck-500 text-white hover:bg-kyck-600',
                    ].join(' ')}
                  >
                    {attach ? 'Add ad to ad set' : 'Publish to Meta'}
                  </button>
                  {publishReason && <p className="mt-1.5 text-xs text-slate-400">{publishReason}</p>}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      {attach ? (
        <AttachPublishConfirmationDialog
          open={confirmOpen}
          target={attach}
          accountName={selectedAccount?.name ?? ''}
          adName={input.ads.name}
          status={input.ads.status}
          publishing={publish.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={confirmPublish}
        />
      ) : (
        <PublishConfirmationDialog
          open={confirmOpen}
          summary={publishSummary}
          warnings={publishWarnings}
          publishing={publish.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={confirmPublish}
        />
      )}

      <PublishSuccessModal
        open={successData !== null}
        publishedMetaAdId={successData?.publishedMetaAdId ?? null}
        adsManagerUrl={metaAdsManagerUrl(
          selectedAccount?.fbAdAccountId ?? null,
          successData?.publishedMetaAdId ?? null,
        )}
        onBackToDrafts={() => navigate('/advertising/meta')}
        onClose={() => setSuccessData(null)}
      />

      <PublishErrorModal
        open={errorOpen}
        publishError={errorData}
        accountId={input.accountId}
        retrying={publish.isPending}
        onClose={() => setErrorOpen(false)}
        onRetry={confirmPublish}
      />

      {dupOpen && (
        <DuplicateModal
          sourceTitle={input.title || 'Untitled'}
          busy={duplicate.isPending}
          onCancel={() => setDupOpen(false)}
          onConfirm={(newTitle) => duplicate.mutate(newTitle)}
        />
      )}

      {/* Build Campaign Phase 2 — Drive creative pipeline (rollout-built drafts). */}
      {pullOpen && draftId && input.driveSource && (
        <PullCreativeModal
          draftId={draftId}
          accountId={input.accountId}
          driveSource={input.driveSource}
          currentInput={input}
          version={versionRef.current}
          onApplied={adoptPulledDraft}
          onClose={() => setPullOpen(false)}
        />
      )}
    </div>
  );
}
